import { parseCampaignList } from "@/lib/utils/campaign";
import {
  clearIndexedDbCacheByPrefix,
  getIndexedDbCacheEntry,
  setIndexedDbCacheEntry,
  registerHeavyCache,
  clearAllHeavyCaches,
  getHeavyCacheMinRemainingMs,
  beginHeavyRefresh,
  endHeavyRefresh,
  INDEXED_CACHE_TTL_MS,
} from "@/lib/utils/indexedDbCache";
import { getAdvertiserMap, type Advertiser } from "./advertiser.api";
import { setCandidateManualEstimation } from "./communications.api";
import { invalidateInfluencerStatusCache } from "./dashboard.api";
import { notifyCampaignInfluencerStatusChanged } from "@/lib/utils/campaignInfluencerStatusEvents";
// 아래 모듈은 import 시 registerHeavyCache 가 자동 호출되어 레지스트리에 등록됨
import "./campaign-results.api";
import webApiAdmin from "@/lib/api-common/webApiAdmin";
import { getNewApiBaseUrl } from "@/lib/api-common/config";

/** One end of a v2 milestone: `date` is `yyyy-MM-dd`, optional `time` is `HH:mm` (24h). */
export interface MilestoneDatePart {
  date: string;
  time?: string;
}

/**
 * Campaign milestone — v2 shape (GGC2-43). The save endpoint's `milestones` field
 * takes this shape (the old v1 `{ dueDate }` is no longer accepted), and campaigns
 * are read back through the `milestonesUnified` field (also this shape). `_id` is
 * assigned by the BE and absent on unsaved rows.
 */
export interface Milestone {
  _id?: string;
  title: string;
  start: MilestoneDatePart;
  end?: MilestoneDatePart;
  adminId?: string;
}

/** Legacy v1 milestone still present on old campaigns (read via `milestonesUnified`, not this). */
export interface MilestoneV1 {
  _id?: string;
  title?: string;
  dueDate?: string;
  adminId?: string;
}

export interface CandidateGroup {
  name: string;
  order: number;
}

export interface Campaign {
  _id: string;
  id?: string;
  name: string;
  status?: string;
  hidden?: boolean;
  active?: boolean;
  /** Exposed to influencers on the influencer site (influencer.ggcontent.com). */
  isPublic?: boolean;
  userId?: string;
  organizationId?: string;
  advertiserId?: string;
  updatedAt?: string;
  createdAt?: string;
  gameTitle?: string;
  commissionRate?: number;
  budget?: number;
  /** 예: USD — 캠페인 목록 진행률 금액 뒤 ($) 표시에 사용 */
  budgetCurrency?: string;
  currency?: string;
  giveaway?: number;
  /** Read milestones from `milestonesUnified` (v2 shape, always current). `milestones`
   *  (v1) and `milestonesV2` (raw stored) are kept only as fallbacks — see
   *  `readCampaignMilestones` — because different BE serialization paths populate
   *  different fields (lean+virtuals → unified; save `toObject()` → v2). */
  milestonesUnified?: Milestone[];
  milestonesV2?: Milestone[];
  milestones?: MilestoneV1[];
  publicationDates?: string[];
  crawlAnalytics?: boolean;
  influencersCountries?: Record<string, number>;
  allowedChannelsTypes?: string[];
  genres?: string[];
  campaignGenre?: string;
  candidateGroups?: CandidateGroup[];
  /** campaign editor */
  alternativeGameTitles?: string[];
  campaignPlatforms?: string[];
  exampleVideos?: string[];
  [key: string]: unknown;
}

export interface BudgetCompletion {
  booked?: number;
  actual?: number;
  viaPayments?: number;
}

export interface CampaignStat {
  byStatus?: Record<string, number>;
  notRead?: number;
  /** API가 직접 반환 시 사용 (원본과 동일 표시) */
  total?: number;
  candidates?: number;
}

export interface ActionLog {
  campaignId: string;
  adminId?: string;
  subAction?: string;
  createdAt?: string;
}

/** campaigns_unchecked_advertiser_action_counts API 응답 형태 */
export type UncheckedActionCounts = Record<string, Record<string, number>>;

export interface CampaignIndexData {
  campaigns: Campaign[];
  /** Commission-excluded (pure) budget completion per campaign. */
  budgetCompletions: Record<string, BudgetCompletion>;
  /** Commission-included budget completion per campaign (BE per-influencer). */
  budgetCompletionsWithCommission: Record<string, BudgetCompletion>;
  campaignStats: Record<string, CampaignStat>;
  actionsLog: Record<string, ActionLog>;
  advertisersById: Record<string, Advertiser>;
  uncheckedCounts: UncheckedActionCounts;
  /** Per-campaign count of unreviewed post candidates (gc-mono parity). */
  newPostCandidatesCountByCampaignId: Record<string, number>;
}

type CampaignListResponse = Campaign[] | { campaigns?: Campaign[] };

const CAMPAIGN_CACHE_KEY_PREFIX = "campaigns.getCampaigns::";
const pendingCampaignRequests = new Map<string, Promise<Campaign[]>>();

const ACTION_LOG_CACHE_KEY_PREFIX = "campaigns.loadActionLog::";
const ACTION_LOG_CACHE_KEY = `${ACTION_LOG_CACHE_KEY_PREFIX}{}`;
let pendingActionLogRequest: Promise<Record<string, ActionLog>> | null = null;

const CAMPAIGN_STATS_CACHE_KEY_PREFIX = "campaigns.getCampaignsStats::";
const pendingCampaignStatsRequests = new Map<
  string,
  Promise<Record<string, CampaignStat>>
>();
const BUDGET_COMPLETION_CACHE_KEY_PREFIX = "api2.campaigns_budget_completion::";
const pendingBudgetCompletionRequests = new Map<
  string,
  Promise<Record<string, BudgetCompletion>>
>();

const UNCHECKED_COUNTS_CACHE_KEY_PREFIX =
  "campaigns.uncheckedAdvertiserActionCounts::";
const pendingUncheckedCountsRequests = new Map<
  string,
  Promise<UncheckedActionCounts>
>();

const NEW_POST_CANDIDATES_COUNT_CACHE_KEY_PREFIX = "campaigns.newPostCandidatesCount::";
const NEW_POST_CANDIDATES_COUNT_CACHE_KEY = `${NEW_POST_CANDIDATES_COUNT_CACHE_KEY_PREFIX}{}`;
let pendingNewPostCandidatesCountRequest: Promise<Record<string, number>> | null = null;

function normalizeCampaignList(
  data: CampaignListResponse | null | undefined,
): Campaign[] {
  return parseCampaignList(data).map((campaign) => ({
    ...campaign,
    id: campaign.id ?? campaign._id,
  }));
}

function normalizeCacheValue(value: unknown): Campaign[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((campaign) => ({
    ...(campaign as Campaign),
    id: (campaign as Campaign).id ?? (campaign as Campaign)._id,
  }));
}

function getStableCacheValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(getStableCacheValue);
  }

  if (value && typeof value === "object") {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        const entryValue = (value as Record<string, unknown>)[key];
        if (typeof entryValue === "undefined") {
          return acc;
        }

        acc[key] = getStableCacheValue(entryValue);
        return acc;
      }, {});
  }

  return value;
}

function getCampaignCacheKey(params: Record<string, unknown>): string {
  return `${CAMPAIGN_CACHE_KEY_PREFIX}${JSON.stringify(getStableCacheValue(params))}`;
}

/* ── 각 캐시를 레지스트리에 등록 (자동으로 clearAll / timer에 포함됨) ── */
registerHeavyCache(CAMPAIGN_CACHE_KEY_PREFIX, async () => {
  pendingCampaignRequests.clear();
  await clearIndexedDbCacheByPrefix(CAMPAIGN_CACHE_KEY_PREFIX);
});
registerHeavyCache(ACTION_LOG_CACHE_KEY_PREFIX, async () => {
  pendingActionLogRequest = null;
  await clearIndexedDbCacheByPrefix(ACTION_LOG_CACHE_KEY_PREFIX);
});
registerHeavyCache(CAMPAIGN_STATS_CACHE_KEY_PREFIX, async () => {
  pendingCampaignStatsRequests.clear();
  await clearIndexedDbCacheByPrefix(CAMPAIGN_STATS_CACHE_KEY_PREFIX);
});
registerHeavyCache(BUDGET_COMPLETION_CACHE_KEY_PREFIX, async () => {
  pendingBudgetCompletionRequests.clear();
  await clearIndexedDbCacheByPrefix(BUDGET_COMPLETION_CACHE_KEY_PREFIX);
});
registerHeavyCache(UNCHECKED_COUNTS_CACHE_KEY_PREFIX, async () => {
  pendingUncheckedCountsRequests.clear();
  await clearIndexedDbCacheByPrefix(UNCHECKED_COUNTS_CACHE_KEY_PREFIX);
});
registerHeavyCache(NEW_POST_CANDIDATES_COUNT_CACHE_KEY_PREFIX, async () => {
  pendingNewPostCandidatesCountRequest = null;
  await clearIndexedDbCacheByPrefix(NEW_POST_CANDIDATES_COUNT_CACHE_KEY_PREFIX);
});
// advertiser.api와 campaign-results.api는 각각 자체 모듈에서 등록됨

/** 등록된 모든 무거운 API 캐시를 삭제 (헤더 버튼 등에서 호출) */
export const clearCampaignCache = clearAllHeavyCaches;

/** 무거운 API 캐시 중 가장 빨리 만료되는 항목까지 남은 시간(ms). 없으면 null. */
export const getHeavyIndexedCacheMinRemainingMs = getHeavyCacheMinRemainingMs;

/**
 * getCampaigns(params)와 동일한 IndexedDB 키에 저장된 목록만 읽는다.
 * 만료 여부와 무관히 표시용 ID→이름 매칭에 사용(미스 시 빈 배열).
 */
export async function getCampaignListFromIndexedDbCache(
  params: Record<string, unknown> = {},
): Promise<Campaign[]> {
  const cacheKey = getCampaignCacheKey(params);
  const cached = await getIndexedDbCacheEntry<Campaign[]>(cacheKey);
  if (!cached?.value) {
    return [];
  }
  return normalizeCacheValue(cached.value);
}

/** POST /api3/campaigns/getCampaigns */
export async function getCampaigns(
  params: Record<string, unknown> = {},
  options: { forceRefresh?: boolean } = {},
): Promise<Campaign[]> {
  const normalizedParams = params ?? {};
  const cacheKey = getCampaignCacheKey(normalizedParams);
  const cachedEntry = options.forceRefresh
    ? null
    : await getIndexedDbCacheEntry<Campaign[]>(cacheKey);

  if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
    return normalizeCacheValue(cachedEntry.value);
  }

  const pendingRequest = pendingCampaignRequests.get(cacheKey);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = (async () => {
    beginHeavyRefresh();
    try {
      const data = await webApiAdmin.post<CampaignListResponse>(
        "admin/campaign/campaigns/get",
        normalizedParams,
      );
      const campaigns = normalizeCampaignList(data);
      if (Array.isArray(campaigns) && campaigns.length > 0) {
        await setIndexedDbCacheEntry(
          cacheKey,
          campaigns,
          INDEXED_CACHE_TTL_MS,
        );
      }
      return campaigns;
    } catch (error) {
      if (cachedEntry?.value) {
        return normalizeCacheValue(cachedEntry.value);
      }
      throw error;
    } finally {
      pendingCampaignRequests.delete(cacheKey);
      endHeavyRefresh();
    }
  })();

  pendingCampaignRequests.set(cacheKey, request);
  return request;
}

/** POST /api3/campaigns/getCampaigns */
export async function getCampaignById(
  campaignId: string,
): Promise<Campaign | null> {
  if (!campaignId) {
    return null;
  }

  const campaigns = await getCampaigns({ campaignId });
  // v2 `campaigns/get` ignores `campaignId` for restricted users (advertisers and admins
  // without `toSeeAllCampaigns`) and returns every allowed campaign sorted by name, so
  // `[0]` could be an arbitrary campaign — pick the requested one explicitly.
  return campaigns.find((c) => (c._id ?? c.id) === campaignId) ?? null;
}

function getBudgetCompletionCacheKey(withCommission: boolean): string {
  return `${BUDGET_COMPLETION_CACHE_KEY_PREFIX}${JSON.stringify(
    getStableCacheValue({ withCommission }),
  )}`;
}

/**
 * Per-campaign budget completion map.
 * - Legacy: POST /api2/campaigns_budget_completion
 *           → `Record<campaignId, { booked?, actual?, viaPayments? }>`
 * - New:    POST /admin/campaign/budget-completion/all-campaigns/get
 *           → `Array<{ campaignId, completion: Array<{ part, amount }> }>`
 *           위 array 를 legacy 의 record 형태로 정규화한다.
 */
export async function getBudgetCompletions(
  withCommission = false,
  options: { forceRefresh?: boolean } = {},
): Promise<Record<string, BudgetCompletion>> {
  const cacheKey = getBudgetCompletionCacheKey(withCommission);
  const cachedEntry = options.forceRefresh
    ? null
    : await getIndexedDbCacheEntry<Record<string, BudgetCompletion>>(cacheKey);

  if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
    return cachedEntry.value;
  }

  const pendingRequest = pendingBudgetCompletionRequests.get(cacheKey);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = (async () => {
    beginHeavyRefresh();
    try {
      const result: Record<string, BudgetCompletion> = {};

      // Admin-only on the BE (403 for advertisers) and the failure is swallowed below,
      // so use the silent variant: the global error toast would fire on every poll.
      const list = await webApiAdmin
        .postSilent<Array<{
          campaignId: string;
          completion: Array<{ part: string; amount: number }>;
        }>>("admin/campaign/budget-completion/all-campaigns/get", { withCommission })
        .catch(() => [] as Array<{ campaignId: string; completion: Array<{ part: string; amount: number }> }>);
      if (Array.isArray(list)) {
        for (const row of list) {
          if (!row?.campaignId) continue;
          const flat: BudgetCompletion = {};
          for (const { part, amount } of row.completion ?? []) {
            (flat as Record<string, number>)[part] = amount;
          }
          result[row.campaignId] = flat;
        }
      }

      if (Object.keys(result).length > 0) {
        await setIndexedDbCacheEntry(
          cacheKey,
          result,
          INDEXED_CACHE_TTL_MS,
        );
      }
      return result;
    } catch (error) {
      if (cachedEntry?.value && Object.keys(cachedEntry.value).length > 0) {
        return cachedEntry.value;
      }
      throw error;
    } finally {
      pendingBudgetCompletionRequests.delete(cacheKey);
      endHeavyRefresh();
    }
  })();

  pendingBudgetCompletionRequests.set(cacheKey, request);
  return request;
}

function normalizeCampaignIdsForStatsCache(campaignIds: string[]): string[] {
  return [...new Set(campaignIds.filter(Boolean))].sort();
}

function getCampaignStatsCacheKey(campaignIds: string[]): string {
  return `${CAMPAIGN_STATS_CACHE_KEY_PREFIX}${JSON.stringify(
    normalizeCampaignIdsForStatsCache(campaignIds),
  )}`;
}

/** POST /api3/campaigns/getCampaignsStats */
export async function getCampaignStats(
  campaignIds: string[],
  options: { forceRefresh?: boolean } = {},
): Promise<Record<string, CampaignStat>> {
  if (!campaignIds.length) {
    return {};
  }

  const cacheKey = getCampaignStatsCacheKey(campaignIds);
  const cachedEntry = options.forceRefresh
    ? null
    : await getIndexedDbCacheEntry<Record<string, CampaignStat>>(cacheKey);

  if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
    return cachedEntry.value;
  }

  const pendingRequest = pendingCampaignStatsRequests.get(cacheKey);
  if (pendingRequest) {
    return pendingRequest;
  }

  const normalizedIds = normalizeCampaignIdsForStatsCache(campaignIds);

  const request = (async () => {
    beginHeavyRefresh();
    try {
      // New BE returns Array<{ campaignId, byStatus, notRead }>. Normalize it to
      // Record<campaignId, CampaignStat> so the rest of the app
      // (e.g. `campaignStats[cid]`) stays untouched.
      const data = await webApiAdmin
        .post<Array<CampaignStat & { campaignId: string }>>("admin/campaign/campaigns-stats/get", {
          campaignIds: normalizedIds,
        })
        .catch(() => [] as Array<CampaignStat & { campaignId: string }>);

      if (!data || typeof data !== "object") {
        return {};
      }

      let result: Record<string, CampaignStat>;
      if (Array.isArray(data)) {
        result = {};
        for (const row of data) {
          if (row?.campaignId) result[row.campaignId] = row;
        }
      } else {
        result = data as Record<string, CampaignStat>;
      }
      if (Object.keys(result).length > 0) {
        await setIndexedDbCacheEntry(
          cacheKey,
          result,
          INDEXED_CACHE_TTL_MS,
        );
      }
      return result;
    } catch (error) {
      if (cachedEntry?.value && Object.keys(cachedEntry.value).length > 0) {
        return cachedEntry.value;
      }
      throw error;
    } finally {
      pendingCampaignStatsRequests.delete(cacheKey);
      endHeavyRefresh();
    }
  })();

  pendingCampaignStatsRequests.set(cacheKey, request);
  return request;
}

function reduceActionLogList(data: ActionLog[]): Record<string, ActionLog> {
  return data.reduce<Record<string, ActionLog>>((acc, entry) => {
    if (!entry.campaignId) {
      return acc;
    }

    acc[entry.campaignId] = entry;
    return acc;
  }, {});
}

/** POST /api3/campaigns/loadActionLog */
export async function getCampaignActionLogMap(
  options: { forceRefresh?: boolean } = {},
): Promise<Record<string, ActionLog>> {
  const cachedEntry = options.forceRefresh
    ? null
    : await getIndexedDbCacheEntry<Record<string, ActionLog>>(ACTION_LOG_CACHE_KEY);

  if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
    return cachedEntry.value;
  }

  if (pendingActionLogRequest) {
    return pendingActionLogRequest;
  }

  pendingActionLogRequest = (async () => {
    beginHeavyRefresh();
    try {
      // Admin-only on the BE (403 for advertisers); failure is swallowed → silent variant.
      const data = await webApiAdmin
        .postSilent<ActionLog[]>("admin/campaign/action-log/load", {})
        .catch(() => []);
      if (!Array.isArray(data)) {
        return {};
      }

      const map = reduceActionLogList(data);
      if (Object.keys(map).length > 0) {
        await setIndexedDbCacheEntry(
          ACTION_LOG_CACHE_KEY,
          map,
          INDEXED_CACHE_TTL_MS,
        );
      }
      return map;
    } catch (error) {
      if (cachedEntry?.value) {
        return cachedEntry.value;
      }
      throw error;
    } finally {
      pendingActionLogRequest = null;
      endHeavyRefresh();
    }
  })();

  return pendingActionLogRequest;
}

function getUncheckedCountsCacheKey(campaignIds: string[]): string {
  return `${UNCHECKED_COUNTS_CACHE_KEY_PREFIX}${JSON.stringify(
    normalizeCampaignIdsForStatsCache(campaignIds),
  )}`;
}

/** POST /api2/campaigns_unchecked_advertiser_action_counts
 * - Legacy: Record<campaignId, Record<action, count>>
 * - New BE: Array<{ campaignId, countByAction: Array<{ action, count }> }>
 *
 * Cached like its siblings in loadCampaignIndexData (getCampaignStats etc.).
 * This is the default sort key for the campaigns dashboard "active" tab, so an
 * uncached call would resolve a full network RTT after the other (cached) batch
 * members, leaving the freshly painted list to visibly re-sort once it lands.
 */
export async function getUncheckedAdvertiserActionCounts(
  campaignIds: string[],
  options: { forceRefresh?: boolean } = {},
): Promise<UncheckedActionCounts> {
  if (!campaignIds.length) return {};

  const cacheKey = getUncheckedCountsCacheKey(campaignIds);
  const cachedEntry = options.forceRefresh
    ? null
    : await getIndexedDbCacheEntry<UncheckedActionCounts>(cacheKey);

  if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
    return cachedEntry.value;
  }

  const pendingRequest = pendingUncheckedCountsRequests.get(cacheKey);
  if (pendingRequest) {
    return pendingRequest;
  }

  type NewBeItem = { campaignId: string; countByAction: { action: string; count: number }[] };
  const normalizedIds = normalizeCampaignIdsForStatsCache(campaignIds);

  const request = (async () => {
    beginHeavyRefresh();
    try {
      const data = await webApiAdmin
        // Admin-only on the BE (403 for advertisers); failure is swallowed → silent variant.
        .postSilent<NewBeItem[]>(
          "admin/campaign/campaigns-unchecked-advertiser-action-counts/get",
          { campaignIds: normalizedIds },
        )
        .catch(() => null);
      if (!Array.isArray(data)) {
        // Request failed — fall back to the last good value instead of clobbering
        // the caller's list with empty counts.
        return cachedEntry?.value ?? {};
      }
      const result: UncheckedActionCounts = {};
      for (const { campaignId, countByAction } of data) {
        result[campaignId] = Object.fromEntries(countByAction.map(({ action, count }) => [action, count]));
      }
      if (Object.keys(result).length > 0) {
        await setIndexedDbCacheEntry(cacheKey, result, INDEXED_CACHE_TTL_MS);
      }
      return result;
    } catch (error) {
      if (cachedEntry?.value && Object.keys(cachedEntry.value).length > 0) {
        return cachedEntry.value;
      }
      throw error;
    } finally {
      pendingUncheckedCountsRequests.delete(cacheKey);
      endHeavyRefresh();
    }
  })();

  pendingUncheckedCountsRequests.set(cacheKey, request);
  return request;
}

export async function loadCampaignIndexData(options?: {
  forceRefresh?: boolean;
  /**
   * 수수료 포함 진행률 변형도 함께 fetch 할지 여부 (캠페인 대시보드 전용).
   * 이 변형은 BE 에서 campaigns 컬렉션 $lookup 조인이 추가되는 무거운 쿼리라,
   * 필요 없는 호출부(로스터 등)에서는 생략한다.
   */
  withCommissionVariant?: boolean;
}): Promise<CampaignIndexData> {
  const forceRefresh = options?.forceRefresh ?? false;
  const includeWcVariant = options?.withCommissionVariant ?? false;
  const campaigns = await getCampaigns({}, { forceRefresh });
  const campaignIds = campaigns.map((campaign) => campaign._id).filter(Boolean);

  const [
    budgetCompletions,
    budgetCompletionsWithCommission,
    campaignStats,
    actionsLog,
    advertisersById,
    newPostCandidatesCountByCampaignId,
    uncheckedCounts,
  ] = await Promise.all([
    // The campaigns dashboard fetches both completion variants up front (separate
    // cache keys) so the commission toggle is instant and the progress popover can
    // show included + excluded side by side. BE applies commission per-influencer,
    // so the included variant must come from the server — not a flat FE multiply.
    // The WC variant is a heavier query, so other consumers (rosters) skip it.
    getBudgetCompletions(false, { forceRefresh }),
    includeWcVariant
      ? getBudgetCompletions(true, { forceRefresh })
      : Promise.resolve({} as Record<string, BudgetCompletion>),
    getCampaignStats(campaignIds, { forceRefresh }),
    getCampaignActionLogMap({ forceRefresh }),
    getAdvertiserMap(undefined, { forceRefresh }),
    // Non-critical: per-campaign "potential" badge count. Swallow on failure
    // so a single 5xx doesn't break the campaigns page bootstrap.
    getNewPostCandidatesCount({ forceRefresh }).catch(() => ({})),
    // Default sort key for the "active" tab. Cached like the others, so it lands
    // together with the batch and the list renders sorted in one pass instead of
    // re-sorting once a lone background request resolves.
    getUncheckedAdvertiserActionCounts(campaignIds, { forceRefresh }),
  ]);

  return {
    campaigns,
    budgetCompletions,
    budgetCompletionsWithCommission,
    campaignStats,
    actionsLog,
    newPostCandidatesCountByCampaignId,
    advertisersById,
    uncheckedCounts,
  };
}

/**
 * Change influencer status on a campaign.
 * - Legacy: POST /api2/change_influencer_status
 * - New:    POST /admin/campaign/influencer-status/change
 */
export async function changeCampaignInfluencerStatus(params: {
  campaignId: string;
  // BE validates each entry as a MongoId (see admin-campaign/actions/changeInfluencerStatus.action.ts) —
  // HistoryRow.userId is never actually populated by the BE (only `id` is), so callers pass `id` here.
  userIds: (string | number)[];
  status: string;
}): Promise<void> {
  const { campaignId, userIds, status } = params;
  if (!campaignId || !userIds.length || !status) {
    return;
  }
  const payload = { campaignId, userIds, status };
  await webApiAdmin.post("admin/campaign/influencer-status/change", payload);
  // getInfluencerStatuses has its own 60s in-memory cache — drop it so the
  // upcoming refetch actually hits the network instead of serving stale counts.
  invalidateInfluencerStatusCache(campaignId);
  // CampaignContextSidebar's "Influencer status" counts are a layout-level
  // sibling with no reference to this call — nudge it to refetch.
  notifyCampaignInfluencerStatusChanged();
}

/**
 * Add influencers to a campaign (as candidates).
 * - Legacy: POST /api2/add_influencers_to_campaign
 * - New:    POST /admin/campaign/influencers/add
 */
export async function addInfluencersToCampaign(params: {
  campaignId: string;
  userIds: (string | number)[];
}): Promise<void> {
  const { campaignId, userIds } = params;
  if (!campaignId || !userIds.length) {
    return;
  }
  const payload = { campaignId, userIds };
  await webApiAdmin.post("admin/campaign/influencers/add", payload);
}

/** POST /api3/campaigns/importInfluencers - 다른 캠페인에서 인플루언서 가져오기 (원본 CampaignTools) */
export async function importInfluencersFromCampaign(params: {
  fromCampaignId: string;
  toCampaignId: string;
  statuses: string[];
  fieldsToCopy: string[];
}): Promise<void> {
  const { fromCampaignId, toCampaignId, statuses, fieldsToCopy } = params;
  if (!fromCampaignId || !toCampaignId || !statuses?.length) return;
  const payload = {
    fromCampaignId,
    toCampaignId,
    statuses,
    fieldsToCopy: fieldsToCopy ?? [],
  };
  await webApiAdmin.post("admin/campaign/influencers/import", payload);
  await clearCampaignCache();
}

/**
 * Update an influencer's price on a campaign (proposal | final | advertiser).
 * - Legacy: POST /api3/campaigns/updateInfluencerPrice
 *           gc-mono action: `Actions.CAMPAIGN.USERS.UPDATE_PRICE`
 * - New:    POST /admin/campaign/influencer-price/update
 *
 * The per-video-type breakdown is a separate concern — see `updateInfluencerPriceBreakdown`
 * below. It does NOT go through this endpoint (no `type: 'breakdown'` here).
 */
export async function updateInfluencerPrice(params: {
  userId: string | number;
  campaignId: string;
  value: number | unknown[];
  type: 'proposal' | 'final' | 'advertiser';
}, _options?: { skipErrorToast?: boolean }): Promise<void> {
  await webApiAdmin.post("admin/campaign/influencer-price/update", params);
}

/** `hour` is for stream-family items; `month`/`year` are for footage-right-only items. */
export type PriceBreakdownDurationUnit = 'hour' | 'day' | 'week' | 'month' | 'year';

/**
 * One row of an influencer's per-video-type price breakdown. Pure additive metadata —
 * it does NOT replace `priceFinal`/`priceAdvertiser`/`candidateManualEstimation.partnership`,
 * which remain the only values used for settlement, budget burn-rate, Excel export, and
 * DocuSign contracts. See `ggc-be` `docs/influencer-price-breakdown.md` for the full
 * BE contract this mirrors.
 */
export interface PriceBreakdownItem {
  /** Required by BE validation on every item (a valid `AdvertisingType` id) — kept
   *  optional here so this same type also fits loosely-typed `.lean()` read responses
   *  (`Influencer['campaigns'][number]['priceBreakdown']`), where every field is
   *  nominally optional. Callers building a save payload must still populate it. */
  partnership?: string;
  priceFinal?: number;
  priceAdvertiser?: number;
  /** Both `count` and `unit` are required together, or omit the whole key — BE rejects
   *  a half-filled duration with a 400. Inner fields stay optional for the same reason
   *  `partnership` above does — this type doubles as the `.lean()` read shape. */
  durationFrom?: { count?: number; unit?: PriceBreakdownDurationUnit };
  durationTo?: { count?: number; unit?: PriceBreakdownDurationUnit };
}

/**
 * Replace an influencer's per-video-type price breakdown on a campaign — a pure
 * whole-array-replace, independent of `updateInfluencerPrice`'s final/advertiser calls
 * (no shared side effects either direction; call order doesn't matter).
 * - New: POST /admin/campaign/influencer-price-breakdown/update
 *
 * BE validation: `partnership` required on every item; `priceFinal`/`priceAdvertiser`/
 * `durationFrom`/`durationTo` are optional but reject `null` (omit the key instead); max
 * 50 items. A validation failure 400s and saves nothing.
 */
export async function updateInfluencerPriceBreakdown(params: {
  userId: string | number;
  campaignId: string;
  priceBreakdown: PriceBreakdownItem[];
}): Promise<void> {
  await webApiAdmin.post("admin/campaign/influencer-price-breakdown/update", params);
}

/**
 * Set an influencer's video type (partnership) on a campaign — writes
 * `userCampaign.candidateManualEstimation.partnership` per influencer.
 * - Legacy: POST /api3/communications/setCandidateManualEstimation
 * - New:    POST /admin/communications/set-candidate-manual-estimation
 *
 * The per-user v2 endpoint supports the `partnership` key (verified against
 * admin-communications/setCandidateManualEstimation), so this reuses the
 * `communications.api` wrapper. Mirrors gc-mono's `UPDATE_VIDEO_TYPE` action.
 */
export async function updateInfluencerVideoType(params: {
  userId: string;
  campaignId: string;
  partnership: string | null;
}): Promise<void> {
  const { userId, campaignId, partnership } = params;
  if (!userId || !campaignId) return;
  await setCandidateManualEstimation({ userId, campaignId, key: "partnership", value: partnership });
}

/**
 * Count unreviewed post candidates per campaign — sidebar badge in gc-mono.
 * - Legacy: POST /api3/campaigns/getNewPostCandidatesCount → Record<campaignId, count>
 * - New:    POST /admin/campaign/new-post-candidates-count/get → Array<{ campaignId, count }>
 *
 * Same IndexedDB-cache + in-flight-dedup pattern as its `loadCampaignIndexData`
 * siblings (getCampaignActionLogMap etc.) — previously this had neither, so every
 * concurrent caller (React 18 StrictMode's double effect-invoke in dev, or the
 * campaigns page's 30s poll firing while a slow request was still in flight)
 * hit the network again instead of sharing one in-flight/cached result.
 */
export async function getNewPostCandidatesCount(
  options: { forceRefresh?: boolean } = {},
): Promise<Record<string, number>> {
  const cachedEntry = options.forceRefresh
    ? null
    : await getIndexedDbCacheEntry<Record<string, number>>(NEW_POST_CANDIDATES_COUNT_CACHE_KEY);

  if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
    return cachedEntry.value;
  }

  if (pendingNewPostCandidatesCountRequest) {
    return pendingNewPostCandidatesCountRequest;
  }

  pendingNewPostCandidatesCountRequest = (async () => {
    beginHeavyRefresh();
    try {
      const data = await webApiAdmin
        // Admin + `toEdit` only on the BE (403 otherwise); failure is swallowed → silent variant.
        .postSilent<Array<{ campaignId: string; count: number }>>(
          "admin/campaign/new-post-candidates-count/get",
          {}
        )
        .catch(() => [] as Array<{ campaignId: string; count: number }>);
      const map = Array.isArray(data) ? Object.fromEntries(data.map((d) => [d.campaignId, d.count])) : {};

      if (Object.keys(map).length > 0) {
        await setIndexedDbCacheEntry(
          NEW_POST_CANDIDATES_COUNT_CACHE_KEY,
          map,
          INDEXED_CACHE_TTL_MS,
        );
      }
      return map;
    } catch (error) {
      if (cachedEntry?.value) {
        return cachedEntry.value;
      }
      throw error;
    } finally {
      pendingNewPostCandidatesCountRequest = null;
      endHeavyRefresh();
    }
  })();

  return pendingNewPostCandidatesCountRequest;
}

/**
 * Static config — game genres available for campaign tagging.
 * - Legacy: POST /api3/campaigns/getGenres (bundled in getEnvironment)
 * - New:    POST /admin/campaign/genres/get
 *
 * v2 FE currently reads from `envs.campaignGenres` (loaded via getEnvironment).
 * This standalone fetcher exists for force-refresh use cases.
 */
export async function getCampaignGenres(): Promise<unknown[]> {
  const data = await webApiAdmin.post<unknown[]>("admin/campaign/genres/get", {});
  return Array.isArray(data) ? data : [];
}

/**
 * Static config — advertising types sorted by display order.
 * - Legacy: POST /api3/campaigns/getAdvertisingTypes (bundled in getEnvironment)
 * - New:    POST /admin/campaign/advertising-types/get
 *
 * Same usage note as `getCampaignGenres`.
 */
export async function getCampaignAdvertisingTypes(): Promise<unknown[]> {
  const data = await webApiAdmin.post<unknown[]>("admin/campaign/advertising-types/get", {});
  return Array.isArray(data) ? data : [];
}

/**
 * Response from `/admin/campaign/save` and `/admin/campaign/save/advertiser`.
 * - New BE (webApiAdmin): returns the full Campaign document; `_id` is the canonical ID.
 * - Legacy BE (webApi3):  returns `{ value: { id } }` (gc-mono convention).
 * Both shapes are surfaced here so callers can use `_id ?? id ?? value?.id`.
 */
export interface SaveCampaignV2Response {
  _id?: string;
  id?: string;
  value?: { id?: string };
  /** Guest-access token; the BE backfills it on save when missing. */
  guestToken?: string;
}

/**
 * Payload for POST /admin/campaign/save.
 *
 * `isNonGame` controls which game-specific fields the BE requires or clears:
 * - `true`  → game fields (alternativeGameTitles, campaignPlatforms, game URLs, etc.) are
 *             not required and will be cleared on save.
 * - `false | undefined` → alternativeGameTitles and campaignPlatforms are required.
 *
 * Added in GGC2-24 (non-game campaign support).
 */
export interface SaveCampaignPayload {
  id: string;
  name: string;
  gameTitle: string;
  /** When true, game-specific fields are cleared and not required. */
  isNonGame?: boolean;
  userId: string;
  status: string;
  advertiserId: string;
  commissionRate: number;
  publicationDates: string[];
  languages: string[];
  allowEmptyLanguages: boolean;
  regions: string[];
  banner: unknown[];
  screenshots: unknown[];
  exampleVideos: unknown[];
  isPublic: boolean;
  isOpenApplications: boolean;
  hidden: boolean;
  allowedChannelsTypes: string[];
  crawlAnalytics: boolean;
  genres: string[];
  milestones: unknown[];
  // Game-specific (required when isNonGame is not true)
  alternativeGameTitles?: string[];
  campaignPlatforms?: string[];
  // Optional fields
  [key: string]: unknown;
}

/**
 * Payload for POST /admin/campaign/save/advertiser.
 *
 * `saveType` is required:
 * - `temporary`   → stores to advertiserTemporaryData side-buffer.
 * - `consolidate` → writes directly to campaign and clears the buffer.
 *
 * `isNonGame` must match the campaign's isNonGame flag. When true, game URL
 * fields (gameWebURL, gameiOSURL, gameAndroidURL, productDescription) are
 * ignored and cleared.
 *
 * Added/updated in GGC2-24 (non-game campaign support).
 */
export interface SaveCampaignAdvertiserPayload {
  saveType: 'consolidate' | 'temporary';
  id: string;
  gameTitle: string;
  /** Must match the campaign's isNonGame flag. When true, game URL fields are cleared. */
  isNonGame?: boolean;
  publicationDates: string[];
  languages: string[];
  regions: string[];
  budgetWithCommission?: number;
  giveawayWithCommission?: number;
  streamLength?: string;
  talkingPoints?: string;
  // Accepted only when isNonGame is not true
  gameWebURL?: string;
  gameiOSURL?: string;
  gameAndroidURL?: string;
  productDescription?: string;
}

/**
 * Save a campaign (full schema).
 * - Legacy: POST /api3/campaigns/saveCampaignV2
 * - New:    POST /admin/campaign/save
 *
 * `isNonGame=true` skips game-specific required fields (GGC2-24).
 */
export async function saveCampaignV2(
  payload: SaveCampaignPayload | Record<string, unknown>,
): Promise<SaveCampaignV2Response> {
  const data = await webApiAdmin.post<SaveCampaignV2Response>("admin/campaign/save", payload as Record<string, unknown>);
  return data ?? {};
}

/** Descriptor returned by the file-upload endpoints. */
export interface UploadedFile {
  url: string;
  filename: string;
  fileId: string;
  type: string;
  size: number;
}

/**
 * A campaign/roster file attachment (`files[]` on the campaign save payload).
 * Mirrors the BE `CampaignFile` schema (antd Upload shape).
 */
export interface CampaignFile {
  name: string;
  size?: number;
  type?: string;
  url: string;
  uid?: string;
  status?: string;
}

/**
 * Upload a roster file (any type, max 25 MB) to the outbound
 * attachment library and return its permanent public-URL descriptor.
 * - New BE: POST /admin/upload/file (multipart `file`)
 *
 * ⚠️ Launch prerequisite: production CloudFront must route /api-v2/* to the
 * prod BE or this returns the SPA index.html.
 */
export async function uploadRosterFile(file: File): Promise<UploadedFile> {
  return webApiAdmin.postFile<UploadedFile>("admin/upload/file", { file });
}

/**
 * Save campaign edits from the advertiser-facing editor (restricted field set).
 * - Legacy: POST /api3/campaigns/saveCampaignV2Advertiser
 *           gc-mono action: `Actions.CAMPAIGN.EDITOR.SAVE2ADVERTISER`
 * - New:    POST /admin/campaign/save/advertiser
 *
 * `saveType` is required. `isNonGame` must match the campaign's flag (GGC2-24).
 *
 * NOTE: v2 FE does not yet expose an advertiser-facing campaign editor;
 * this wrapper exists so when that page is ported the endpoint is ready.
 */
export async function saveCampaignAdvertiser(
  payload: SaveCampaignAdvertiserPayload,
): Promise<SaveCampaignV2Response> {
  const data = await webApiAdmin.post<SaveCampaignV2Response>("admin/campaign/save/advertiser", payload as unknown as Record<string, unknown>);
  return data ?? {};
}

/**
 * Partial campaign update (genre + platforms only — legacy contract).
 * - Legacy: POST /api3/campaigns/updateCampaignV2Partially
 * - New:    POST /admin/campaign/update/partially
 */
export async function updateCampaignFields(params: {
  campaignId: string;
  value: Record<string, unknown>;
}): Promise<SaveCampaignV2Response> {
  const { campaignId, value } = params;
  if (!campaignId || !Object.keys(value).length) {
    return {};
  }
  const payload = { id: campaignId, ...value };
  const data = await webApiAdmin.post<SaveCampaignV2Response>("admin/campaign/update/partially", payload);
  return data ?? {};
}

/**
 * Save the campaign-level `candidateManualEstimation.<key>` (views / budget).
 * - New: POST /admin/campaign/candidate-manual-estimation/set ({ campaignId, key, value })
 *   (`admin/campaign/save` requires the full campaign payload and never writes this field.)
 */
export async function setCampaignManualEstimation(params: {
  campaignId: string;
  key: 'views' | 'budget';
  value: number | null;
}): Promise<void> {
  const { campaignId, key, value } = params;
  if (!campaignId || !key) return;
  await webApiAdmin.post("admin/campaign/candidate-manual-estimation/set", {
    campaignId,
    key,
    value,
  });
}

/** GET /admin/communications/excel/:campaignId/candidates - xlsx 다운로드 URL (v2) */
export function getCandidatesExcelUrl(
  campaignId: string,
  timezone: string,
): string {
  return `${getNewApiBaseUrl()}/admin/communications/excel/${campaignId}/candidates?timezone=${encodeURIComponent(timezone)}`;
}

/** GET /admin/campaign/:id/influencers/excel - 캠페인 인플루언서 목록 xlsx 다운로드 URL (v2) */
export function getCampaignInfluencersExcelUrl(
  campaignId: string,
  filter?: Record<string, unknown>,
  priceWithCommission = true,
): string {
  const params = new URLSearchParams();
  params.set('priceWithCommission', String(priceWithCommission));
  if (filter && Object.keys(filter).length > 0) {
    params.set('filter', JSON.stringify(filter));
  }
  return `${getNewApiBaseUrl()}/admin/campaign/${campaignId}/influencers/excel?${params.toString()}`;
}

/** 캠페인 자료 폴더 (campaign.materialsFolder) - 원본 CampaignMaterials */
export interface MaterialsFolder {
  fileId?: string;
  name?: string;
  url?: string;
  isPublic?: boolean;
}

/** 광고주용 문서 폴더 (campaign.advertisersMaterialsFolder) */
export interface AdvertisersMaterialsFolder {
  fileId?: string;
  name?: string;
  url?: string;
  isPublic?: boolean;
}

/**
 * Create the materials Google Drive folder for a campaign.
 * - Legacy: POST /api3/campaigns/createFolder
 * - New:    POST /admin/campaign/folder/create
 */
export async function createMaterialsFolder(
  campaignId: string,
): Promise<MaterialsFolder> {
  // Both endpoints return the full campaign (the folder is nested at
  // campaign.materialsFolder), not the folder itself — extract it.
  const campaign = await webApiAdmin.post<{ materialsFolder?: MaterialsFolder }>("admin/campaign/folder/create", { campaignId });
  await clearCampaignCache();
  return campaign?.materialsFolder ?? {};
}

/**
 * Delete the campaign's materials Google Drive folder.
 * - Legacy: POST /api3/campaigns/deleteFolder
 * - New:    POST /admin/campaign/folder/delete
 */
export async function deleteMaterialsFolder(
  campaignId: string,
): Promise<void> {
  await webApiAdmin.post("admin/campaign/folder/delete", { campaignId });
  await clearCampaignCache();
}

/**
 * Toggle public visibility of the campaign's materials folder.
 * - Legacy: POST /api3/campaigns/changeFolderVisibility
 * - New:    POST /admin/campaign/folder-visibility/change
 */
export async function changeMaterialsFolderVisibility(
  campaignId: string,
  visibility: boolean,
): Promise<void> {
  const payload = { campaignId, visibility };
  await webApiAdmin.post("admin/campaign/folder-visibility/change", payload);
  await clearCampaignCache();
}

/**
 * Create the advertiser-facing materials folder for a campaign.
 * - Legacy: POST /api3/campaigns/createAdvertisersFolder
 * - New:    POST /admin/campaign/folder/advertisers/create
 */
export async function createAdvertisersMaterialsFolder(
  campaignId: string,
): Promise<AdvertisersMaterialsFolder> {
  // Both endpoints return the full campaign (the folder is nested at
  // campaign.advertisersMaterialsFolder), not the folder itself — extract it.
  const campaign = await webApiAdmin.post<{ advertisersMaterialsFolder?: AdvertisersMaterialsFolder }>(
    "admin/campaign/folder/advertisers/create",
    { campaignId },
  );
  await clearCampaignCache();
  return campaign?.advertisersMaterialsFolder ?? {};
}

/**
 * Delete the campaign's advertiser-facing materials folder.
 * - Legacy: POST /api3/campaigns/deleteAdvertisersFolder
 * - New:    POST /admin/campaign/folder/advertisers/delete
 */
export async function deleteAdvertisersMaterialsFolder(
  campaignId: string,
): Promise<void> {
  await webApiAdmin.post("admin/campaign/folder/advertisers/delete", { campaignId });
  await clearCampaignCache();
}

/**
 * Toggle public visibility of the advertiser-facing materials folder.
 * - Legacy: POST /api3/campaigns/changeAdvertisersFolderVisibility
 *           gc-mono action: `Actions.CAMPAIGN.ADVERTISERSFOLDER.CHANGE_VISIBILITY`
 * - New:    POST /admin/campaign/folder-visibility/advertisers/change
 *
 * v2 FE does not yet expose this toggle; the wrapper is kept ready for
 * the materials panel UI.
 */
export async function changeAdvertisersMaterialsFolderVisibility(
  campaignId: string,
  visibility: boolean,
): Promise<void> {
  const payload = { campaignId, visibility };
  await webApiAdmin.post("admin/campaign/folder-visibility/advertisers/change", payload);
  await clearCampaignCache();
}

/** POST /api3/campaigns/getGoogleDriveFolderTree */
export interface GoogleDriveFileNode {
  id?: string;
  name?: string;
  isFolder?: boolean;
  files?: GoogleDriveFileNode[];
  modifiedTime?: string;
  size?: string;
}

/**
 * List Google Drive folder tree.
 * - Legacy: POST /api3/campaigns/getGoogleDriveFolderTree
 * - New:    POST /admin/campaign/google-drive-folder-tree/get
 */
export async function getGoogleDriveFolderTree(
  fileId: string,
): Promise<GoogleDriveFileNode[]> {
  const data = await webApiAdmin.post<GoogleDriveFileNode[]>(
    "admin/campaign/google-drive-folder-tree/get",
    { fileId },
  );
  return Array.isArray(data) ? data : [];
}

/**
 * Load historical personal-comment tokens (silent — empty on failure).
 * - Legacy: POST /api3/campaigns/loadOldCommentTokens
 * - New:    POST /admin/campaign/old-comment-tokens/load
 */
export async function loadOldCommentTokens(): Promise<string[]> {
  // v2 BE returns the bare token array (`ApiAction<string[]>`), not `{ tokens }`.
  const res = await webApiAdmin
    .postSilent<string[]>("admin/campaign/old-comment-tokens/load")
    .catch(() => [] as string[]);
  return Array.isArray(res) ? res : [];
}

/**
 * Save comment tokens for a user (default or per-campaign).
 * - Legacy: POST /api3/campaigns/saveCommentTokens
 * - New:    POST /admin/campaign/comment-tokens/save
 *
 * campaignId: 'default' → saves user.suggestedComment (requires toAccessAllFeatures)
 * campaignId: <objectId> → saves userCampaign.comment
 */
export async function saveCommentTokens(
  userId: string | number,
  tokens: string[],
  options: { campaignId: string; applyToSiblings?: boolean },
): Promise<string | null> {
  const { campaignId, applyToSiblings } = options;
  const payload = campaignId === 'default'
    ? { userId, tokens, campaignId: 'default', applyToSiblings: applyToSiblings ?? false }
    : { userId, tokens, campaignId };
  const res = await webApiAdmin.post<{ comment?: string | null }>(
    "admin/campaign/comment-tokens/save", payload,
  );
  return res?.comment ?? null;
}

/**
 * Soft-delete a campaign.
 * - Legacy: POST /api3/campaigns/deleteCampaign
 * - New:    POST /admin/campaign/delete
 */
export async function deleteCampaign(campaignId: string): Promise<void> {
  await webApiAdmin.post("admin/campaign/delete", { campaignId });
  await clearCampaignCache();
}

/**
 * Hide or unhide a campaign.
 * - Legacy: POST /api3/campaigns/changeCampaignVisibility
 * - New:    POST /admin/campaign/visibility/change
 */
export async function changeCampaignVisibility(campaignId: string, hidden: boolean): Promise<void> {
  const payload = { campaignId, hidden };
  await webApiAdmin.post("admin/campaign/visibility/change", payload);
}

/**
 * Move a campaign to draft or private-draft status.
 * - Legacy: POST /api3/campaigns/moveToStatus
 * - New:    POST /admin/campaign/status/move-to
 */
export async function moveCampaignToStatus(campaignId: string, status: 'private_draft' | 'draft'): Promise<void> {
  const payload = { campaignId, status };
  await webApiAdmin.post("admin/campaign/status/move-to", payload);
}

/**
 * Suggest comment tokens for an influencer based on past campaigns (silent).
 * - Legacy: POST /api3/campaigns/loadRecommendedCommentTokens
 * - New:    POST /admin/campaign/recommended-comment-tokens/load
 */
export async function loadRecommendedCommentTokens(userId: string | number): Promise<{
  mostPopularCommentsFromPreviousCampaigns?: string[];
  influencerCommentsFromPreviousCampaigns?: string[];
}> {
  type RecommendedTokens = {
    mostPopularCommentsFromPreviousCampaigns?: string[];
    influencerCommentsFromPreviousCampaigns?: string[];
  };
  const res = await webApiAdmin
    .postSilent<RecommendedTokens>("admin/campaign/recommended-comment-tokens/load", { userId })
    .catch(() => ({}) as RecommendedTokens);
  return res ?? {};
}
